export const GRID_SIZE = 5;

export type GridPosition = {
  readonly row: number;
  readonly column: number;
};

export type GridDirection = "up" | "right" | "down" | "left";

const DIRECTION_VECTORS: Readonly<Record<GridDirection, GridPosition>> = {
  up: { row: -1, column: 0 },
  right: { row: 0, column: 1 },
  down: { row: 1, column: 0 },
  left: { row: 0, column: -1 },
};

export function moveGridFocus(
  current: GridPosition,
  direction: GridDirection,
  existingFields: readonly GridPosition[],
): GridPosition {
  assertPosition(current, "current focus");
  const existing = toPositionSet(existingFields);
  const vector = DIRECTION_VECTORS[direction];
  let candidate = add(current, vector);

  while (isOnBoard(candidate)) {
    if (existing.has(positionKey(candidate))) {
      return candidate;
    }
    candidate = add(candidate, vector);
  }

  return current;
}

export function recoverGridFocus(
  lostFocus: GridPosition,
  existingFields: readonly GridPosition[],
  preferred?: GridPosition,
): GridPosition | undefined {
  assertPosition(lostFocus, "lost focus");
  const existing = toPositionSet(existingFields);

  if (preferred !== undefined) {
    assertPosition(preferred, "preferred focus");
    if (existing.has(positionKey(preferred))) {
      return preferred;
    }
  }

  return [...existingFields].sort(
    (left, right) =>
      manhattanDistance(left, lostFocus) - manhattanDistance(right, lostFocus) ||
      left.row - right.row ||
      left.column - right.column,
  )[0];
}

export function positionKey(position: GridPosition): string {
  return `${position.row}:${position.column}`;
}

function toPositionSet(positions: readonly GridPosition[]): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const position of positions) {
    assertPosition(position, "existing field");
    const key = positionKey(position);
    if (keys.has(key)) {
      throw new Error(`Duplicate existing field at ${key}.`);
    }
    keys.add(key);
  }
  return keys;
}

function assertPosition(position: GridPosition, label: string): void {
  if (
    !Number.isInteger(position.row) ||
    !Number.isInteger(position.column) ||
    !isOnBoard(position)
  ) {
    throw new RangeError(
      `${label} must be an integer position on the ${GRID_SIZE} by ${GRID_SIZE} board.`,
    );
  }
}

function isOnBoard(position: GridPosition): boolean {
  return (
    position.row >= 0 &&
    position.row < GRID_SIZE &&
    position.column >= 0 &&
    position.column < GRID_SIZE
  );
}

function add(left: GridPosition, right: GridPosition): GridPosition {
  return { row: left.row + right.row, column: left.column + right.column };
}

function manhattanDistance(left: GridPosition, right: GridPosition): number {
  return Math.abs(left.row - right.row) + Math.abs(left.column - right.column);
}
